import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import api from "@/lib/api";
import WhatsAppLink from "@/components/WhatsAppLink";
import BackNav from "@/components/BackNav";
import { eur, Loading } from "@/components/common";

export default function Servizi() {
  const [flowers, setFlowers] = useState(null);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    api.get("/flowers").then((r) => setFlowers(r.data)).catch(() => setFlowers([]));
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  if (!flowers) return <Loading />;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 space-y-14">
      <BackNav />
      <div className="max-w-2xl">
        <p className="eyebrow mb-2">I nostri servizi</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-navy mb-4">Cura semplice, fatta bene.</h1>
        <p className="text-body leading-relaxed">
          Il servizio principale è il cambio dei fiori: ogni intervento comprende sempre la cura ordinaria
          del luogo, con la possibilità di aggiungere una pulizia approfondita. Per esigenze particolarmente
          impegnative valutiamo insieme una pulizia specialistica.
        </p>
      </div>

      <section data-testid="flower-catalog-section">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="eyebrow mb-1">Catalogo fiori</p>
            <h2 className="font-serif text-3xl text-navy">Cambio fiori</h2>
          </div>
          <p className="text-sm text-muted-soft max-w-md">
            Il Mazzo di Margherite ({eur(config?.flower_standard_price ?? 6)}) è incluso nell'intervento. I prezzi mostrati sono i prezzi finali delle proposte.
          </p>
        </div>
        <p className="text-xs text-muted-soft leading-relaxed mb-6 -mt-2" data-testid="seasonality-note">
          Il colore e le varietà possono variare in base alla disponibilità e alla stagionalità.
          Puoi indicare una preferenza nelle note al momento della richiesta: faremo il possibile per rispettarla.
        </p>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {flowers.map((f) => (
            <div key={f.id} className="card-soft overflow-hidden" data-testid={`flower-card-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <img src={f.image} alt={f.name} className="w-full aspect-[4/3] object-cover" loading="lazy" />
              <div className="p-5">
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <p className="font-semibold text-navy">{f.name}</p>
                  <p className="font-serif text-xl text-navy">{eur(f.price)}</p>
                </div>
                <p className="text-sm text-body leading-relaxed">{f.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-5 lg:grid-cols-3">
        <div className="card-soft p-8 border-2 !border-[#d1e2d5]" data-testid="cleaning-included-card">
          <p className="eyebrow mb-1">Sempre compresa</p>
          <h3 className="font-serif text-2xl text-navy mb-1">Cura ordinaria <span className="text-[#4a7c59]">inclusa</span></h3>
          <p className="text-sm text-body mb-5">Quando effettuiamo un intervento, ci prendiamo cura del luogo anche attraverso queste piccole attenzioni. Senza costi aggiuntivi.</p>
          <ul className="space-y-1.5 text-sm text-body">
            {["Rimozione di foglie e polvere", "Rimozione dello sporco leggero", "Piccolo riordino del luogo", "Semplice risciacquo, quando necessario e appropriato", "Controllo visivo generale"].map((x) => (
              <li key={x} className="flex items-start gap-2"><Check size={15} className="text-[#4a7c59] mt-0.5 shrink-0" />{x}</li>
            ))}
          </ul>
        </div>
        <div className="card-soft p-8" data-testid="cleaning-deep-card">
          <p className="eyebrow mb-1">Servizio aggiuntivo</p>
          <h3 className="font-serif text-2xl text-navy mb-1">Pulizia approfondita <span className="text-[#4a7c59]">+{eur(config?.cleaning_ordinary_price ?? 15)}</span></h3>
          <p className="text-sm text-body mb-5">Un'attenzione in più, eseguita con prodotti professionali e specifici per il tipo di superficie.</p>
          <ul className="space-y-1.5 text-sm text-body">
            {["Pulizia più accurata della lapide", "Pulizia di statue e piccoli elementi decorativi", "Pulizia dei lumi", "Prodotti specifici e appropriati per marmo e superfici", "Rimozione dello sporco più persistente", "Riordino finale"].map((x) => (
              <li key={x} className="flex items-start gap-2"><Check size={15} className="text-[#4a7c59] mt-0.5 shrink-0" />{x}</li>
            ))}
          </ul>
        </div>
        <div className="card-ivory p-8 flex flex-col justify-between" data-testid="cleaning-specialist-card">
          <div>
            <p className="eyebrow mb-1">Su valutazione</p>
            <h3 className="font-serif text-2xl text-navy mb-2">Pulizia specialistica <span className="text-[#4a7c59] text-lg">· Preventivo</span></h3>
            <p className="text-sm text-body leading-relaxed">
              Per interventi particolarmente impegnativi possiamo valutare la necessità di un trattamento
              specialistico e, quando necessario, affidarci a professionisti qualificati, con prodotti
              professionali e trattamenti specifici per il tipo di superficie.
            </p>
          </div>
          <WhatsAppLink
            message={"Ciao, ti contatto tramite Sempre Accanto perché vorrei richiedere un preventivo per una pulizia profonda/specialistica del luogo in cui effettuare il servizio. Vorrei avere maggiori informazioni e sapere il costo indicativo. Grazie."}
            label="Richiedi un preventivo"
            testid="specialist-cleaning-cta"
            className="btn-secondary mt-6 self-start"
          />
        </div>
      </section>

      <div className="text-center space-y-4 pb-6">
        <p className="font-serif text-2xl text-navy">Pronto ad affidare la cura a noi?</p>
        <div className="flex justify-center gap-3 flex-wrap">
          <Link to="/singola-uscita" data-testid="services-single-cta" className="btn-primary">Prova una singola uscita</Link>
          <Link to="/abbonamenti" data-testid="services-sub-cta" className="btn-secondary">Scopri gli abbonamenti</Link>
        </div>
      </div>
    </div>
  );
}
